import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { CatalogSong } from '../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../common/types';
import { MasterPlayButton } from '../player/MasterPlayButton';
import { MasterPlayerBar } from '../player/MasterPlayerBar';
import { MasterDownloadButton } from '../player/MasterDownloadButton';
import { masterPlayerMessages } from '../player/masterPlayerMessages';
import { useMasterPlayer } from '../player/useMasterPlayer';
import { SONG_PAGE_SECTIONS, SongPageSection, songPageSectionId } from './catalogSongPath';
import { SongChipTone, SongStatusPill } from './SongPageChips';
import { songPageMessages } from './songPageMessages';
import { Panel } from '../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from './SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Scrolls a section of the song page into view. */
export function scrollToSongSection(section: SongPageSection): void {
  window.requestAnimationFrame(() => {
    document.getElementById(songPageSectionId(section))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

const SECTION_LABELS = {
  details: songPageMessages.sectionDetails,
  files: songPageMessages.sectionFiles,
  lyrics: songPageMessages.sectionLyrics,
  release: songPageMessages.sectionRelease,
} as const;

/** The section that is at the top of the window now. No `IntersectionObserver` (a test): the first. */
function useActiveSection(): SongPageSection {
  const [active, setActive] = useState<SongPageSection>('details');
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        const first = SONG_PAGE_SECTIONS.find((section) =>
          visible.some((entry) => entry.target.id === songPageSectionId(section))
        );
        if (first !== undefined) {
          setActive(first);
        }
      },
      { rootMargin: '-140px 0px -55% 0px' }
    );
    SONG_PAGE_SECTIONS.forEach((section) => {
      const element = document.getElementById(songPageSectionId(section));
      if (element !== null) {
        observer.observe(element);
      }
    });
    return () => observer.disconnect();
  }, []);
  return active;
}

interface SongPageBarProps {
  accountId: number;
  itemUuid: string;
  song: CatalogSong | undefined;
}

/**
 * The bar that stays at the top while the page scrolls (owner decision 2: one long page): the
 * master player of design b1 (the Lyrics section needs it at every line) and the links to the
 * sections, each with its state pill (the tab pills of the design). No master: no player.
 */
const SongPageBarComponent: React.FC<SongPageBarProps> = ({ accountId, itemUuid, song }) => {
  const intl = useIntl();
  const player = useMasterPlayer({ accountId, itemUuid });
  const active = useActiveSection();
  const master = song?.master ?? null;
  const canPlay = master !== null && (master.canPlay ?? master.status === 'ready');
  const hasPlayer = master !== null && master.status !== 'failed';

  const pill = useCallback(
    (section: SongPageSection): { tone: SongChipTone; label: string } | null => {
      if (song === undefined) {
        return null;
      }
      if (section === 'files') {
        if (song.master === null) {
          return { tone: 'off', label: intl.formatMessage(songPageMessages.navNone) };
        }
        if (song.master.status === 'ready') {
          return { tone: 'ok', label: intl.formatMessage(songPageMessages.navReady) };
        }
        return song.master.status === 'failed'
          ? { tone: 'err', label: intl.formatMessage(songPageMessages.chipMasterFailed) }
          : { tone: 'run', label: intl.formatMessage(songPageMessages.chipMasterReading) };
      }
      if (section === 'lyrics') {
        if (song.lyrics === null || song.lyrics.trim() === '') {
          return { tone: 'off', label: intl.formatMessage(songPageMessages.navNone) };
        }
        const status = song.lyricTiming?.status;
        if (status === 'synced') {
          return { tone: 'ok', label: intl.formatMessage(songPageMessages.navSynced) };
        }
        if (status === 'syncing') {
          return { tone: 'run', label: intl.formatMessage(songPageMessages.chipLyricsSyncing) };
        }
        return status === 'failed'
          ? { tone: 'err', label: intl.formatMessage(songPageMessages.chipLyricsFailed) }
          : null;
      }
      return null;
    },
    [intl, song]
  );

  return (
    <Panel className={styles.bar} data-testid="song-page-bar">
      {hasPlayer && master !== null && (
        <div className={styles.player} data-testid="song-page-player">
          <MasterPlayButton status={player.status} disabled={!canPlay} small onToggle={player.toggle} />
          <div className={styles.playerTrack}>
            <MasterPlayerBar
              positionSec={player.positionSec}
              durationSec={player.durationSec ?? master.durationSec}
              hookStartSec={master.hookStartSec}
              disabled={!canPlay}
              note={canPlay ? undefined : intl.formatMessage(masterPlayerMessages.processing)}
              onSeek={player.seek}
            />
          </div>
          <MasterDownloadButton accountId={accountId} itemUuid={itemUuid} fileName={master.name} />
        </div>
      )}
      <nav aria-label={intl.formatMessage(songPageMessages.sectionNav)}>
        <ul className={styles.nav}>
          {SONG_PAGE_SECTIONS.map((section) => {
            const state = pill(section);
            return (
              <li key={section}>
                <a
                  href={`#${songPageSectionId(section)}`}
                  className={clsx(styles.navLink, active === section && styles.navLinkOn)}
                  aria-current={active === section ? 'true' : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    scrollToSongSection(section);
                  }}
                >
                  {intl.formatMessage(SECTION_LABELS[section])}
                  {state !== null && (
                    <SongStatusPill tone={state.tone} label={state.label} className={styles.navPill} />
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </Panel>
  );
};

export const SongPageBar = TypedMemo(SongPageBarComponent);
